import { useFeatureFlagEnabled } from "../../../common/posthog";
import * as R from "ramda";
import { useEffect, useState } from "react";

import { FEATURE_FLAG_LENS_MODEL_SELECTOR } from "../../../common/features";
import {
  AVAILABLE_MODELS,
  PRODUCTION_MODEL
} from "../../../services/claude";
import {
  PromptTemplate,
  PromptTemplateCreationParams,
  PromptTemplateScope
} from "../../../types/prompt-template";
import usePromptTemplates from "../../explore/hooks/usePromptTemplates";
import { LensFormErrorKey, LensFormErrors, resolveInitialFormData, saveTemplate, validateLensForm } from "../utils";

interface UseLensEditorParams {
  template: PromptTemplate | null;
  defaultTemplate: PromptTemplateCreationParams;
  onRefetch: () => void;
  onClose: () => void;
}

interface LensEditorContext {
  header: string;
  action: string;
  showTemplateInfo: boolean;
  formData: PromptTemplateCreationParams;
  formErrors: LensFormErrors;
  loading: boolean;
  availableModels: readonly typeof AVAILABLE_MODELS[number][];
  onFieldChange: (field: string, value: string | number | boolean) => void;
  onMetadataChange: (field: string, value: string) => void;
  onModelConfigChange: (field: string, value: string | number) => void;
  onScopeChange: (value: PromptTemplateScope[]) => void;
  onSubmit: () => Promise<void>;
}

const useLensEditor = ({
  template, defaultTemplate, onRefetch, onClose
}: UseLensEditorParams): LensEditorContext => {
  const { createTemplate, updateTemplate, loading } = usePromptTemplates({ autoFetch: false });
  const availableModels = useFeatureFlagEnabled(FEATURE_FLAG_LENS_MODEL_SELECTOR) ? AVAILABLE_MODELS : [PRODUCTION_MODEL];

  const [formData, setFormData] = useState<PromptTemplateCreationParams>(defaultTemplate);
  const [formErrors, setFormErrors] = useState<LensFormErrors>({});

  useEffect((): void => {
    setFormErrors({});
    setFormData(resolveInitialFormData(template, defaultTemplate));
  }, [template, defaultTemplate]);

  const onFieldChange = (field: string, value: string | number | boolean): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => R.dissoc(field as LensFormErrorKey, prev));
  };

  const onMetadataChange = (field: string, value: string): void => {
    setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, [field]: value } }));
    setFormErrors(prev => R.dissoc(`metadata.${field}` as LensFormErrorKey, prev));
  };

  const onScopeChange = (value: PromptTemplateScope[]): void => {
    setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, scope: value } }));
    setFormErrors(prev => R.dissoc("metadata.scope" as LensFormErrorKey, prev));
  };

  const onModelConfigChange = (field: string, value: string | number): void => {
    setFormData(prev => ({ ...prev, model_config: { ...prev.model_config, [field]: value } }));
    setFormErrors(prev => R.dissoc(`model_config.${field}` as LensFormErrorKey, prev));
  };

  const onSubmit = async (): Promise<void> => {
    const errors = validateLensForm(formData);
    if (!R.isEmpty(errors)) {
      setFormErrors(errors);
      return;
    }
    try {
      await saveTemplate({ template, formData, createTemplate, updateTemplate });
      onRefetch();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save template";
      setFormErrors({ submit: message });
    }
  };

  return {
    header: template ? "Edit Lens" : "Create New Lens",
    action: template ? "Save" : "Create",
    showTemplateInfo: !!template,
    formData, formErrors, loading, availableModels,
    onFieldChange, onMetadataChange, onModelConfigChange, onScopeChange, onSubmit
  };
};

export default useLensEditor;
